import PropTypes from "prop-types"
import React, { useCallback, useEffect, useState } from "react"

import { Label } from "../Label"
import { RadioContainer as Container, ItemContainer as Item, RadioButton as Radio } from "./styled"

/**
 * A group of label enabled radio buttons
 * It has an internal state or can be controlled by changes on initialValue
 *
 * Radio buttons can be initially checked by props
 */
const RadioButtonGroup = ({
  items = [],
  handleChange,
  inline = false,
  name,
  children,
  initialValue,
  className,
  spaciousClick = true,
}) => {
  const [checkedValue, setValue] = useState(initialValue)

  useEffect(() => {
    setValue(initialValue)
  }, [initialValue])

  const onChange = (event) => {
    const { value } = event.target

    if (handleChange) {
      handleChange(value, event)
    }

    setValue(value)
  }

  const handleItemClick = useCallback(
    (value, disabled, event) => {
      if (spaciousClick && !disabled) {
        if (handleChange) {
          handleChange(value, event)
        }

        setValue(value)
      }
    },
    [spaciousClick, handleChange],
  )

  return (
    <Container $inline={inline} className={className}>
      {items.map(({ id, value, disabled, label, contents }, radioKey) => (
        <Item key={radioKey} onClick={(e) => handleItemClick(value, disabled, e)}>
          <Radio
            id={id}
            key={id}
            name={name}
            value={value}
            checked={checkedValue === value}
            disabled={disabled}
            onChange={onChange}
          />
          {contents}
          {label && (
            <Label $inline htmlFor={id}>
              {label}
            </Label>
          )}
        </Item>
      ))}
      {children}
    </Container>
  )
}

RadioButtonGroup.propTypes = {
  /**
   * Array of radio buttons
   */
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      disabled: PropTypes.bool,
      label: PropTypes.string,
    }),
  ),
  /**
   * Will be fired with (value,SyntheticEvent)
   */
  handleChange: PropTypes.func,
  /**
   * Will be applied to all radio buttons and put on the html input
   */
  name: PropTypes.string.isRequired,
  /**
   * If true items are put side by side
   */
  inline: PropTypes.bool,
  /**
   * the initial value, if matched with an item value will make it checked
   */
  initialValue: PropTypes.any,
  /**
   * If true, the radio is not only activated on direct click of the radio or its label, but also on click
   * of the components container / the radio item.
   */
  spaciousClick: PropTypes.bool,
}

export { RadioButtonGroup }
